import { ref, computed } from 'vue'
import axios from 'axios'

/**
 * Composable para gerenciar templates de notificação
 */
export function useNotificationTemplates() {
  const templates = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Estado do modal
  const mostrarModal = ref(false)
  const modoEdicao = ref(false)
  const templateSelecionado = ref(null)

  // Variáveis disponíveis para substituição
  const variaveisDisponiveis = ref([])

  /**
   * Carregar lista de templates
   */
  async function carregarTemplates(filtros = {}) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      if (filtros.canal) params.append('canal', filtros.canal)
      if (filtros.ativo !== undefined) params.append('ativo', filtros.ativo)
      if (filtros.search) params.append('search', filtros.search)

      const response = await axios.get(`/api/notificacoes/templates?${params}`)
      templates.value = response.data
      return templates.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao carregar templates'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Carregar variáveis disponíveis
   */
  async function carregarVariaveis() {
    try {
      const response = await axios.get('/api/notificacoes/templates-variaveis/lista')
      variaveisDisponiveis.value = response.data.variaveis
      return variaveisDisponiveis.value
    } catch (err) {
      console.error('Erro ao carregar variáveis:', err)
      return {}
    }
  }

  /**
   * Criar novo template
   */
  async function criarTemplate(dados) {
    loading.value = true
    try {
      const response = await axios.post('/api/notificacoes/templates', dados)
      templates.value.unshift(response.data.template)
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Atualizar template existente
   */
  async function atualizarTemplate(id, dados) {
    loading.value = true
    try {
      const response = await axios.put(`/api/notificacoes/templates/${id}`, dados)
      const index = templates.value.findIndex(t => t.id === id)
      if (index !== -1) {
        templates.value[index] = response.data.template
      }
      return response.data
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Excluir template
   */
  async function excluirTemplate(id) {
    loading.value = true
    try {
      await axios.delete(`/api/notificacoes/templates/${id}`)
      templates.value = templates.value.filter(t => t.id !== id)
      return true
    } catch (err) {
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Preview do template
   */
  async function previewTemplate(id, pacienteId = null) {
    try {
      const data = pacienteId ? { paciente_id: pacienteId } : {}
      const response = await axios.post(`/api/notificacoes/templates/${id}/preview`, data)
      return response.data
    } catch (err) {
      throw err
    }
  }

  // Funções do modal
  function abrirModalCriar() {
    templateSelecionado.value = {
      nome: '',
      canal: 'email',
      assunto: '',
      corpo: '',
      variaveis: [],
      usar_ia: false,
      prompt_ia: '',
      modelo_ia: 'gpt-4.1-mini',
      temperatura: 0.8,
      ativo: true,
    }
    modoEdicao.value = false
    mostrarModal.value = true
  }

  function abrirModalEditar(template) {
    templateSelecionado.value = { ...template }
    modoEdicao.value = true
    mostrarModal.value = true
  }

  function fecharModal() {
    mostrarModal.value = false
    templateSelecionado.value = null
    modoEdicao.value = false
  }

  // Computed
  const templatesAtivos = computed(() => templates.value.filter(t => t.ativo))
  const templatesPorCanal = computed(() => {
    return (canal) => templates.value.filter(t => t.canal === canal || t.canal === 'ambos')
  })

  return {
    // State
    templates,
    loading,
    error,
    mostrarModal,
    modoEdicao,
    templateSelecionado,
    variaveisDisponiveis,

    // Computed
    templatesAtivos,
    templatesPorCanal,

    // Actions
    carregarTemplates,
    carregarVariaveis,
    criarTemplate,
    atualizarTemplate,
    excluirTemplate,
    previewTemplate,

    // Modal
    abrirModalCriar,
    abrirModalEditar,
    fecharModal,
  }
}
